import React from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Container,
  Card,
  Table,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useGetSupportAttentionQuery,
  type SupportAttentionClientRow,
} from "@/features/accounts/accountsApi";
import styles from "./client-support.module.scss";

function formatWhen(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function supportCasesPath(masterAccountId: string): string {
  return `/accounts?client=${encodeURIComponent(masterAccountId)}&panel=support`;
}

const ClientSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isFetching, isError, error, refetch } =
    useGetSupportAttentionQuery({ maxItems: 500 });

  const clients = data?.clients || [];
  const showPartial =
    data != null && (data.exact === false || data.truncated === true);

  const openClient = (row: SupportAttentionClientRow) => {
    navigate(supportCasesPath(row.masterAccountId));
  };

  return (
    <Container fluid>
      <Card className={styles.card}>
        <Card.Body>
          <Card.Title className={styles.cardTitle}>
            <PageTitle title="Client Support" />
          </Card.Title>
          <p className={styles.helper}>
            Active Clients with Support history. Identity comes from the live
            Master Account; counts come from Support Cases for that account.
          </p>

          {showPartial ? (
            <Alert variant="warning" className="py-2">
              Counts may be partial — the Client Support roster returned a
              truncated result set. Totals reflect scanned cases only.
            </Alert>
          ) : null}

          {isError ? (
            <Alert variant="danger" className="py-2">
              {(error as { data?: { message?: string } })?.data?.message ||
                (error as { message?: string })?.message ||
                "Failed to load Client Support"}{" "}
              <button
                type="button"
                className="btn btn-link btn-sm p-0 align-baseline"
                onClick={() => void refetch()}
              >
                Retry
              </button>
            </Alert>
          ) : null}

          {isFetching && !data ? (
            <div className="d-flex align-items-center gap-2 py-4">
              <Spinner animation="border" size="sm" />
              <span className="text-muted">Loading Client Support…</span>
            </div>
          ) : clients.length === 0 && !isError ? (
            <p className="text-muted mb-0 py-3">
              No Clients currently need support.
            </p>
          ) : clients.length > 0 ? (
            <div className={styles.tableWrap}>
              <Table responsive hover className={styles.table}>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Customer #</th>
                    <th>Open</th>
                    <th>In Progress</th>
                    <th>Total</th>
                    <th>Oldest Open</th>
                    <th>Latest activity</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((row) => (
                    <tr key={row.masterAccountId}>
                      <td>
                        <button
                          type="button"
                          className={styles.clientLink}
                          onClick={() => openClient(row)}
                        >
                          {row.businessName || row.masterAccountId}
                        </button>
                      </td>
                      <td className="font-monospace">
                        {row.customerNumber || "—"}
                      </td>
                      <td>{row.openCount}</td>
                      <td>{row.inProgressCount}</td>
                      <td>
                        {row.totalCaseCount != null
                          ? row.totalCaseCount
                          : row.totalNeedingAttention}
                      </td>
                      <td>{formatWhen(row.oldestCreatedAt)}</td>
                      <td>{formatWhen(row.latestUpdatedAt)}</td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => openClient(row)}
                        >
                          View Support Cases
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : null}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default withAuthenticationRequired(ClientSupportPage);
